// Was (2026-10-20): import { Component, HostListener, Injector, OnDestroy, afterNextRender, effect, inject } from '@angular/core';
import { Component, ElementRef, HostListener, Injector, OnDestroy, afterNextRender, effect, inject, viewChild } from '@angular/core';
import { ImageConfirmService } from '../../services/image-confirm.service';
import { formatImageSize } from '../../services/icon-library';
import { GlobalVariablesService } from '../../services/global-variables.service';
import { BodyPortalDirective } from '../../shared/directives/body-portal.directive';

/** "image/webp" reads as "WebP" to a builder. */
const FORMAT_NAMES: Record<string, string> = {
  'image/png': 'PNG', 'image/jpeg': 'JPEG', 'image/webp': 'WebP', 'image/gif': 'GIF', 'image/svg+xml': 'SVG',
};

/**
 * The preview the builder sees when a picture had to be compressed: the file as uploaded beside what will be
 * stored. Mounted once at the app root and answers ImageConfirmService.
 *
 * It opens from inside other overlays — the property panel's image manager (portaled to <body> at z-index 9999),
 * the Spotlight dock (211) and its Atlas (250) — so it is portaled to <body> too and sits above all of them, or the
 * builder would be asked a question they cannot see.
 */
/**
 * The dialog is coloured by the editor's own theme toggle, not Tailwind's `dark:` variant: in this setup that follows
 * the operating system, which the editor never does. Each string is complete so the class scan sees it.
 */
interface Look {
  box: string; title: string; hint: string; side: string; sideTitle: string; picture: string; facts: string;
  compressed: string; compressedTitle: string; compressedPicture: string; cancel: string; use: string;
}
const DARK: Look = {
  box: 'w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl bg-gray-900 shadow-2xl border border-gray-700',
  title: 'text-lg font-semibold text-gray-100',
  hint: 'text-xs text-gray-400 mt-1',
  side: 'rounded-lg border border-gray-700 bg-gray-800 p-3',
  sideTitle: 'text-sm font-medium text-gray-200 mb-2',
  picture: 'flex items-center justify-center h-48 rounded-md bg-gray-950 border border-gray-700 overflow-hidden',
  facts: 'mt-3 space-y-1 text-xs text-gray-300',
  compressed: 'rounded-lg border border-blue-800 bg-blue-950 p-3',
  compressedTitle: 'text-sm font-medium text-blue-200 mb-2',
  compressedPicture: 'flex items-center justify-center h-48 rounded-md bg-gray-950 border border-blue-800 overflow-hidden',
  cancel: 'px-4 py-2 rounded-lg text-sm font-medium text-gray-200 bg-gray-800 hover:bg-gray-700 border border-gray-700',
  use: 'px-4 py-2 rounded-lg text-sm font-medium text-white bg-blue-500 hover:bg-blue-400',
};
const LIGHT: Look = {
  box: 'w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-2xl border border-gray-200',
  title: 'text-lg font-semibold text-gray-900',
  hint: 'text-xs text-gray-500 mt-1',
  side: 'rounded-lg border border-gray-200 bg-gray-50 p-3',
  sideTitle: 'text-sm font-medium text-gray-700 mb-2',
  picture: 'flex items-center justify-center h-48 rounded-md bg-white border border-gray-200 overflow-hidden',
  facts: 'mt-3 space-y-1 text-xs text-gray-600',
  compressed: 'rounded-lg border border-blue-200 bg-blue-50 p-3',
  compressedTitle: 'text-sm font-medium text-blue-800 mb-2',
  compressedPicture: 'flex items-center justify-center h-48 rounded-md bg-white border border-blue-200 overflow-hidden',
  cancel: 'px-4 py-2 rounded-lg text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 border border-gray-200',
  use: 'px-4 py-2 rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700',
};

@Component({
  selector: 'app-image-confirm',
  standalone: true,
  imports: [BodyPortalDirective],
  template: `
  @if (svc.request(); as r) {
    <!-- Was (2026-10-20): <div data-test="image-confirm-backdrop" (click)="cancel()" class="fixed inset-0 z-[130] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"> -->
    <div appBodyPortal data-test="image-confirm-backdrop" (click)="cancel()"
         class="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div role="dialog" aria-modal="true" aria-labelledby="image-confirm-title" (click)="$event.stopPropagation()"
           [class]="look.box">
        <!-- Was (2026-10-20): every box, text, picture frame and button below carried Tailwind dark: variants, which follow the OS colour scheme. -->
        <div class="px-6 pt-5">
          <h2 id="image-confirm-title" [class]="look.title">This image was too large</h2>
          <p [class]="look.hint">It has been compressed to fit. Nothing is saved until you choose.</p>
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 px-6 py-4">
          <div data-test="image-confirm-original" [class]="look.side">
            <p [class]="look.sideTitle">Original</p>
            <div [class]="look.picture">
              <img [src]="r.change.original.dataUrl" alt="Original" class="max-h-full max-w-full object-contain">
            </div>
            <dl [class]="look.facts">
              <div class="flex justify-between"><dt>Size</dt><dd data-test="size">{{ size(r.change.original.bytes) }}</dd></div>
              <div class="flex justify-between"><dt>Pixels</dt><dd data-test="pixels">{{ r.change.original.width }} × {{ r.change.original.height }}</dd></div>
              <div class="flex justify-between"><dt>Format</dt><dd data-test="format">{{ format(r.change.original.type) }}</dd></div>
            </dl>
          </div>
          <div data-test="image-confirm-compressed" [class]="look.compressed">
            <p [class]="look.compressedTitle">Compressed</p>
            <div [class]="look.compressedPicture">
              <img [src]="r.change.result.dataUrl" alt="Compressed" class="max-h-full max-w-full object-contain">
            </div>
            <dl [class]="look.facts">
              <div class="flex justify-between"><dt>Size</dt><dd data-test="size">{{ size(r.change.result.bytes) }}</dd></div>
              <div class="flex justify-between"><dt>Pixels</dt><dd data-test="pixels">{{ r.change.result.width }} × {{ r.change.result.height }}</dd></div>
              <div class="flex justify-between"><dt>Format</dt><dd data-test="format">{{ format(r.change.result.type) }}</dd></div>
              @if (r.change.result.quality !== undefined) {
                <div class="flex justify-between"><dt>Quality</dt><dd data-test="quality">{{ quality(r.change.result.quality) }}</dd></div>
              }
            </dl>
          </div>
        </div>
        <div class="flex justify-end gap-2 px-6 pb-5">
          <button type="button" data-test="image-confirm-cancel" (click)="cancel()"
                  [class]="look.cancel">Cancel</button>
          <button #useButton type="button" data-test="image-confirm-use" (click)="use()"
                  [class]="look.use">Use compressed</button>
        </div>
      </div>
    </div>
  }
  `,
})
export class ImageConfirmComponent implements OnDestroy {
  protected readonly svc = inject(ImageConfirmService);
  private readonly theme = inject(GlobalVariablesService);
  protected get look(): Look { return this.theme.pageThemeDark ? DARK : LIGHT; }
  private readonly detach = this.svc.attachHost();
  private readonly injector = inject(Injector);
  /** What had focus when the question opened, so closing it hands focus back instead of dropping it on the page. */
  private opener: HTMLElement | null = null;
  /** This dialog's own button: a page-wide search could find another dialog's. */
  private readonly useButton = viewChild<ElementRef<HTMLButtonElement>>('useButton');

  constructor() {
    effect(() => {
      if (this.svc.request()) {
        if (!this.opener) this.opener = document.activeElement as HTMLElement | null;
        // The button exists only after this render; "Use compressed" is the expected answer, so Enter takes it.
        // Was (2026-10-20): afterNextRender(() => document.querySelector<HTMLElement>('[data-test="image-confirm-use"]')?.focus(), { injector: this.injector });
        afterNextRender(() => this.useButton()?.nativeElement.focus(), { injector: this.injector });
      } else if (this.opener) {
        const back = this.opener;
        this.opener = null;
        if (document.contains(back)) back.focus();
      }
    });
  }

  ngOnDestroy(): void { this.detach(); }

  protected size(bytes: number): string { return formatImageSize(bytes); }
  protected format(type: string): string { return FORMAT_NAMES[type] ?? type; }
  protected quality(q: number): string { return `${Math.round(q * 100)}%`; }

  // Was (2026-10-20): @HostListener('document:keydown.escape')
  // Was (2026-10-20): onEscape(): void { if (this.svc.request()) this.cancel(); }
  @HostListener('document:keydown.escape', ['$event'])
  onEscape(event: Event): void {
    if (!this.svc.request()) return;
    // Recorded before answering: the answer clears the request, and a listener that runs after this one must still
    // see that the key was spent here.
    this.svc.consume(event);
    this.cancel();
  }

  use(): void { this.svc.request()?.answer(true); }
  cancel(): void { this.svc.request()?.answer(false); }
}
